import type { Ctx, MilkdownPlugin } from '@milkdown/kit/ctx';
import { createTimer } from '@milkdown/kit/ctx';
import { nodesCtx, schemaTimerCtx } from '@milkdown/kit/core';
import { expectDomTypeError } from '@milkdown/exception';
import { IMAGE_DATA_TYPE, imageBlockSchema } from '@milkdown/kit/component/image-block';
import type { NodeSchema } from '@milkdown/kit/transformer';

import { formatSizeTitle, isPresent, parseImageBlock } from './parse';

const IMAGE_BLOCK_ID = 'image-block';

/** Schema waits on this so we can replace `image-block` in `nodesCtx` before Schema builds. */
const PlatformImageBlockSchemaReady = createTimer('PlatformImageBlockSchemaReady');

function patchImageBlockSchema(base: NodeSchema): NodeSchema {
  return {
    ...base,
    attrs: {
      ...base.attrs,
      width: { default: undefined },
      height: { default: undefined },
    },
    parseDOM: [
      {
        tag: `img[data-type="${IMAGE_DATA_TYPE}"]`,
        getAttrs: (dom) => {
          if (!(dom instanceof HTMLElement)) throw expectDomTypeError(dom);
          const w = dom.getAttribute('width');
          const h = dom.getAttribute('height');

          return {
            src: dom.getAttribute('src') || '',
            caption: dom.getAttribute('caption') || '',
            ratio: Number(dom.getAttribute('ratio') ?? 1),
            width: w ? parseInt(w, 10) : undefined,
            height: h ? parseInt(h, 10) : undefined,
          };
        },
      },
    ],
    toDOM: (node) => {
      const { width, height, ...rest } = node.attrs;

      return [
        'img',
        {
          'data-type': IMAGE_DATA_TYPE,
          ...rest,
          ...(isPresent(width) && { width }),
          ...(isPresent(height) && { height }),
        },
      ];
    },
    parseMarkdown: {
      match: ({ type }) => type === 'image-block',
      runner: (state, node, type) => {
        const src = node.url as string;
        const n = node as {
          alt?: string;
          title?: string;
          width?: number;
          height?: number;
        };
        const { ratio, caption, width, height } = parseImageBlock(n.alt, n.title, n.width, n.height);
        state.addNode(type, {
          src,
          caption,
          ratio,
          width,
          height,
        });
      },
    },
    toMarkdown: {
      match: (node) => node.type.name === 'image-block',
      runner: (state, node) => {
        // Platform title format includes a leading space: " =WxH"
        let title = '';
        if (isPresent(node.attrs.width) || isPresent(node.attrs.height)) {
          title = ` ${formatSizeTitle(node.attrs.width, node.attrs.height)}`;
        }
        state.openNode('paragraph');
        state.addNode('image', undefined, undefined, {
          title,
          url: node.attrs.src,
          alt: node.attrs.caption || '',
        });
        state.closeNode();
      },
    },
  };
}

function replaceImageBlockInNodesCtx(ctx: Ctx, nodeSchema: NodeSchema): void {
  const entry: [string, NodeSchema] = [IMAGE_BLOCK_ID, nodeSchema];
  ctx.update(nodesCtx, (ns) => [...ns.filter(([id]) => id !== IMAGE_BLOCK_ID), entry]);
}

/**
 * Replace Crepe's `image-block` node spec with Platform width/height support.
 *
 * Why not `extendSchema()` / prepare-time `ctx.set` alone:
 * - `extendSchema()` registers a second `$node('image-block')`; load uses `Promise.all` → race.
 * - Prepare-time factory patch can lose if `$node` already snapshotted, or if slice identity differs.
 *
 * Instead we gate Schema on {@link PlatformImageBlockSchemaReady}, wait until Crepe registers
 * `image-block` in `nodesCtx`, then overwrite that entry before Schema builds.
 */
export const platformImageBlockSchema: MilkdownPlugin = (ctx) => {
  ctx.record(PlatformImageBlockSchemaReady);
  ctx.update(schemaTimerCtx, (timers) => timers.concat(PlatformImageBlockSchemaReady));

  return async () => {
    for (let attempts = 0; attempts < 100; attempts++) {
      if (ctx.get(nodesCtx).some(([id]) => id === IMAGE_BLOCK_ID)) break;
      await Promise.resolve();
    }

    try {
      const prevFactory = ctx.get(imageBlockSchema.key);
      const nextFactory = (c: Ctx) => patchImageBlockSchema(prevFactory(c));
      ctx.set(imageBlockSchema.key, nextFactory);
      replaceImageBlockInNodesCtx(ctx, nextFactory(ctx));
    } catch {
      // Fallback when Crepe's schema slice identity differs from our kit import.
      const existing = ctx.get(nodesCtx).find(([id]) => id === IMAGE_BLOCK_ID);
      if (existing) {
        replaceImageBlockInNodesCtx(ctx, patchImageBlockSchema(existing[1]));
      }
    }

    ctx.done(PlatformImageBlockSchemaReady);

    return () => {
      ctx.update(schemaTimerCtx, (timers) => timers.filter((t) => t !== PlatformImageBlockSchemaReady));
      ctx.clearTimer(PlatformImageBlockSchemaReady);
    };
  };
};
